import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import "../CSS/GamesMain.css";
import { useNavigate } from "react-router-dom";

const games = [
  {
    id: 1,
    title: "Float Block: Bloxorz Puzzle",
    tag: "Puzzle · Logic · Calm",
    img: "/assets/images/game1.jpg",
    platform: "Android",
    link: "https://play.google.com/store/apps/details?id=com.floatkeygames.floatblock",
    linkPage: "/floatblock",
    active: 1,
  },
  {
    id: 2,
    title: "Float Drop: Endless Fall",
    tag: "Arcade · Reflex · Chill",
    img: "/assets/images/game2.png",
    platform: "Android",
    link: "",
    linkPage: "",
    active: 0,
  },
];

const getRandomX = () =>
  typeof window !== "undefined"
    ? Math.random() * window.innerWidth
    : Math.random() * 400;

const getRandomY = () =>
  typeof window !== "undefined"
    ? Math.random() * window.innerHeight
    : Math.random() * 800;

export default function GamesMain() {
  const navigate = useNavigate();

  const handleCardClick = (game) => {
    const isActive = game.active === 1;
    if (!isActive || !game.linkPage) return;
    navigate(game.linkPage);
  };

  return (
    <section
      id="games"
      className="relative w-full lg:min-h-screen flex flex-col items-center justify-start md:justify-center overflow-hidden px-4 sm:px-6 md:px-0 py-14 md:py-0"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {[...Array(10)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute w-[3px] h-[3px] bg-indigo-400 rounded-full blur-[2px] opacity-40"
            initial={{
              x: getRandomX(),
              y: getRandomY(),
              scale: 0,
            }}
            animate={{
              y: [null, Math.random() * -80],
              opacity: [0.2, 0.6, 0.2],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 7 + Math.random() * 5,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* MOBILE (< md) */}
      <div className="z-10 max-w-md md:hidden flex flex-col items-center my-[100px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="text-center mb-10 isolate w-full"
        >
          <div className="flex justify-center mb-6">
            <img
              src="/assets/images/icon1.png"
              alt="Game controller icon"
              className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-full p-1 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
            />
          </div>

          <div className="relative inline-block isolate">
            <h2
              className="
                relative z-10 
                text-3xl sm:text-4xl 
                font-extrabold tracking-tight
                bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400    
                bg-clip-text text-transparent       
              "
            >
              Our Games
            </h2>
          </div>

          <p
            className="
              mt-7 text-slate-300 
              text-sm sm:text-base
              max-w-md w-full 
              leading-relaxed px-4
            "
          >
            Explore our growing collection of games from finished experiences to
            upcoming worlds still being crafted with care and precision.
          </p>

          <div className="mt-8 mx-auto w-32 h-[3px] bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
        </motion.div>

        <div className="w-full grid grid-cols-1 gap-8 justify-items-center">
          {games.map((game, index) => {
            const hasCardLink = game.active === 1 && game.linkPage;
            const hasExternalLink = game.active === 1 && game.link;

            return (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => handleCardClick(game)}
                className={`
                  w-[250px]
                  group relative rounded-3xl overflow-hidden bg-white/5 backdrop-blur-2xl 
                  border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.4)] 
                  hover:shadow-[0_8px_60px_rgba(99,102,241,0.25)] 
                  hover:border-indigo-400/50 transition-all duration-700
                  ${hasCardLink ? "cursor-pointer" : "cursor-default"}
                `}
                role={hasCardLink ? "button" : undefined}
                aria-label={
                  hasCardLink ? `Open details for ${game.title}` : undefined
                }
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={game.img}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1600ms] ease-[cubic-bezier(.23,1,.32,1)]"
                  />
                  <div
                    className="
                      absolute inset-0 bg-gradient-to-t 
                      from-black/70 via-black/40 to-transparent 
                      opacity-70 group-hover:opacity-90 
                      transition-all duration-700
                    "
                  />
                </div>

                <div className="p-4 flex flex-col gap-3">
                  <div>
                    <span className="text-[0.7rem] tracking-wider uppercase text-indigo-300 font-medium">
                      {game.tag}
                    </span>
                    <h3
                      className="
                        mt-1 
                        text-lg
                        font-semibold text-white 
                        group-hover:text-indigo-400 
                        transition-colors duration-300
                      "
                    >
                      {game.title}
                    </h3>
                  </div>

                  <div
                    className="
                      flex 
                      w-full
                      items-center 
                      justify-between 
                      text-xs text-slate-400
                    "
                  >
                    <span>{game.platform}</span>

                    {hasExternalLink ? (
                      <a
                        href={game.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-indigo-400 font-medium transition-all duration-300 group-hover:text-indigo-300"
                      >
                        Play Store <ExternalLink size={15} />
                      </a>
                    ) : game.active === 1 ? (
                      <span className="text-slate-400 italic">More info</span>
                    ) : (
                      <span className="text-slate-500 italic">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/20 via-purple-500/15 to-transparent blur-2xl" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-5 h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent" />
      </div>

      {/* TABLET (md ≤ width < lg) */}
      <div className="relative z-10 w-full max-w-3xl hidden md:flex lg:hidden flex-col items-center my-[180px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="text-center mb-16 isolate w-full max-w-3xl"
        >
          <div className="flex justify-center mb-6">
            <img
              src="/assets/images/icon1.png"
              alt="Game controller icon"
              className="w-16 h-16 md:w-20 md:h-20 object-contain rounded-full p-1 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
            />
          </div>

          <div className="relative inline-block isolate">
            <div
              className="absolute inset-0 blur-3xl opacity-70 z-0"
              style={{
                background:
                  "linear-gradient(270deg, #6366f1, #8b5cf6, #3b82f6, #a855f7)",
                backgroundSize: "800% 800%",
                animation: "gradientFlow 8s ease infinite",
              }}
            />
            <h2
              className="
                relative z-10 
                text-[38px] md:text-[5xl]
                font-extrabold tracking-tight
                bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400
                bg-[length:300%_100%]
                bg-clip-text text-transparent 
                drop-shadow-[0_0_10px_rgba(99,102,241,0.5)] 
                animate-gradient-x
              "
            >
              Our Games
            </h2>
          </div>

          <p
            className="
              mt-6 text-slate-300 
              text-base md:text-[17px]
              max-w-2xl mx-auto 
              leading-relaxed px-[20px]
            "
          >
            Explore our growing collection of games from finished experiences to
            upcoming worlds still being crafted with care and precision.
          </p>

          <div className="mt-8 mx-auto w-36 h-[3px] bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
        </motion.div>

        <div
          className="
            w-full 
            grid grid-cols-1 md:grid-cols-2 
            gap-8 md:gap-10 
            px-[70px]
          "
        >
          {games.map((game, index) => {
            const hasCardLink = game.active === 1 && game.linkPage;
            const hasExternalLink = game.active === 1 && game.link;

            return (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => handleCardClick(game)}
                className={`
                  group relative rounded-3xl overflow-hidden bg-white/5 backdrop-blur-2xl 
                  border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.4)] 
                  hover:shadow-[0_8px_60px_rgba(99,102,241,0.25)] 
                  hover:border-indigo-400/50 transition-all duration-700
                  ${hasCardLink ? "cursor-pointer" : "cursor-default"}
                `}
                role={hasCardLink ? "button" : undefined}
                aria-label={
                  hasCardLink ? `Open details for ${game.title}` : undefined
                }
              >
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img
                    src={game.img}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1600ms] ease-[cubic-bezier(.23,1,.32,1)]"
                  />
                  <div
                    className="
                      absolute inset-0 bg-gradient-to-t 
                      from-black/70 via-black/40 to-transparent 
                      opacity-70 group-hover:opacity-90 
                      transition-all duration-700
                    "
                  />
                </div>

                <div className="p-5 flex flex-col justify-between gap-3 h-[140px]">
                  <div>
                    <span className="text-xs tracking-wider uppercase text-indigo-300 font-medium">
                      {game.tag}
                    </span>
                    <h3
                      className="
                        mt-1 
                        text-xl 
                        font-semibold text-white 
                        group-hover:text-indigo-400 
                        transition-colors duration-300
                      "
                    >
                      {game.title}
                    </h3>
                  </div>

                  <div
                    className="
                      flex flex-row 
                      items-center 
                      justify-between 
                      mt-2 
                      text-sm text-slate-400
                    "
                  >
                    <span>{game.platform}</span>

                    {hasExternalLink ? (
                      <a
                        href={game.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-indigo-400 font-medium transition-all duration-300 group-hover:text-indigo-300"
                      >
                        Play Store <ExternalLink size={15} />
                      </a>
                    ) : game.active === 1 ? (
                      <span className="text-slate-400 italic">More info</span>
                    ) : (
                      <span className="text-slate-500 italic">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/20 via-purple-500/15 to-transparent blur-2xl" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-[-140px] h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent" />
      </div>

      {/* DESKTOP (lg+) */}
      <div className="relative z-10 w-full max-w-2xl hidden lg:flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="text-center mb-16 isolate max-w-3xl w-full"
        >
          <div className="flex justify-center mb-6">
            <img
              src="/assets/images/icon1.png"
              alt="Game controller icon"
              className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 object-contain rounded-full p-1 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
            />
          </div>

          <div className="relative inline-block isolate">
            <div
              className="absolute inset-0 blur-3xl opacity-70 z-0"
              style={{
                background:
                  "linear-gradient(270deg, #6366f1, #8b5cf6, #3b82f6, #a855f7)",
                backgroundSize: "800% 800%",
                animation: "gradientFlow 8s ease infinite",
              }}
            />
            <h2
              className="
                relative z-10 
                text-4xl sm:text-5xl md:text-6xl 
                font-extrabold tracking-tight
                bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400
                bg-[length:300%_100%]
                bg-clip-text text-transparent 
                drop-shadow-[0_0_10px_rgba(99,102,241,0.5)] 
                animate-gradient-x
              "
            >
              Our Games
            </h2>
          </div>

          <p
            className="
              mt-6 text-slate-300 
              text-sm sm:text-base md:text-lg 
              max-w-2xl mx-auto 
              leading-relaxed
            "
          >
            Explore our growing collection of games from finished experiences to
            upcoming worlds still being crafted with care and precision.
          </p>

          <div className="mt-8 mx-auto w-36 h-[3px] bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
        </motion.div>

        <div
          className="
            w-full 
            grid-cols-1 sm:grid-cols-2 
            gap-8 md:gap-10 
            px-1 sm:px-6
            grid
          "
        >
          {games.map((game, index) => {
            const hasCardLink = game.active === 1 && game.linkPage;
            const hasExternalLink = game.active === 1 && game.link;

            return (
              <motion.div
                key={game.id}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => handleCardClick(game)}
                className={`
                  w-[290px] mx-auto sm:w-full
                  group relative rounded-3xl overflow-hidden bg-white/5 backdrop-blur-2xl 
                  border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.4)] 
                  hover:shadow-[0_8px_60px_rgba(99,102,241,0.25)] 
                  hover:border-indigo-400/50 transition-all duration-700
                  ${hasCardLink ? "cursor-pointer" : "cursor-default"}
                `}
                role={hasCardLink ? "button" : undefined}
                aria-label={
                  hasCardLink ? `Open details for ${game.title}` : undefined
                }
              >
                <div className="relative h-36 sm:h-44 md:h-56 overflow-hidden">
                  <img
                    src={game.img}
                    alt={game.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1600ms] ease-[cubic-bezier(.23,1,.32,1)]"
                  />
                  <div
                    className="
                      absolute inset-0 bg-gradient-to-t 
                      from-black/70 via-black/40 to-transparent 
                      opacity-70 group-hover:opacity-90 
                      transition-all duration-700
                    "
                  />
                </div>

                <div className="p-3 sm:p-5 md:p-7 flex flex-col justify-between gap-3 md:gap-0 h-auto md:h-[200px]">
                  <div>
                    <span className="text-[0.65rem] sm:text-xs tracking-wider uppercase text-indigo-300 font-medium">
                      {game.tag}
                    </span>
                    <h3
                      className="
                        mt-1 
                        text-base sm:text-xl md:text-2xl 
                        font-semibold text-white 
                        group-hover:text-indigo-400 
                        transition-colors duration-300
                      "
                    >
                      {game.title}
                    </h3>
                  </div>

                  <div
                    className="
                      flex flex-col sm:flex-row 
                      items-start sm:items-center 
                      justify-between 
                      gap-1 sm:gap-0
                      mt-2 md:mt-4 
                      text-[0.7rem] sm:text-sm text-slate-400
                    "
                  >
                    <span>{game.platform}</span>

                    {hasExternalLink ? (
                      <a
                        href={game.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-indigo-400 font-medium transition-all duration-300 group-hover:text-indigo-300"
                      >
                        Play Store <ExternalLink size={15} />
                      </a>
                    ) : game.active === 1 ? (
                      <span className="text-slate-400 italic">More info</span>
                    ) : (
                      <span className="text-slate-500 italic">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/20 via-purple-500/15 to-transparent blur-2xl" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
